import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { subscribeToLabels, backfillLabels } from "../../firebase/tasks";
import { useTaskRole } from "../../hooks/useTaskRole";

/**
 * LabelSearchPage
 * Route: /tasks/labels/search
 * Access: TaskUser + TaskAdmin
 *
 * Provides a search input that autocompletes against Firestore labels
 * (prefix match). Selecting a label navigates to LabelFilterView at
 * /tasks/label/:labelName which shows tasks with full inline-edit TaskRow.
 * No option to create labels is provided.
 */
export function LabelSearchPage() {
  const { isTaskAdmin } = useTaskRole();
  const [allLabels, setAllLabels] = useState([]);
  const [query, setQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const history = useHistory();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Run backfill migration if the current user is a TaskAdmin
  useEffect(() => {
    if (isTaskAdmin) {
      backfillLabels().catch((err) => console.error("Label backfill error", err));
    }
  }, [isTaskAdmin]);

  // Subscribe to real-time label list from Firestore
  useEffect(() => {
    const unsubscribe = subscribeToLabels((labels) => {
      setAllLabels(labels);
    });
    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsDropdownOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const trimmedQuery = query.trim().toLowerCase();

  // Prefix match against all labels
  const matchedLabels = trimmedQuery
    ? allLabels.filter((label) =>
        label.name.toLowerCase().startsWith(trimmedQuery)
      )
    : [];

  const noMatch = trimmedQuery.length > 0 && matchedLabels.length === 0;

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setIsDropdownOpen(true);
    setActiveIndex(-1);
  };

  const handleSelectLabel = (labelName) => {
    setIsDropdownOpen(false);
    setQuery("");
    history.push(`/tasks/label/${encodeURIComponent(labelName)}`);
  };

  const handleKeyDown = (e) => {
    if (!isDropdownOpen || matchedLabels.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < matchedLabels.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < matchedLabels.length) {
        handleSelectLabel(matchedLabels[activeIndex].name);
      }
    } else if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div
      className="min-h-screen py-10 px-4 sm:px-6 md:px-8 max-w-xl mx-auto"
      style={{ backgroundColor: "var(--theme-color-canvas)" }}
    >
      {/* Page heading */}
      <div className="mb-8">
        <h1
          className="text-3xl font-extrabold tracking-tight m-0 mb-1"
          style={{
            color: "var(--theme-color-ink)",
            fontFamily: "var(--theme-font-family-display)",
          }}
        >
          Labels
        </h1>
        <p className="text-sm m-0" style={{ color: "var(--theme-color-body)" }}>
          Search for a label to view all associated tasks.
        </p>
      </div>

      {/* Search box + dropdown container */}
      <div className="position-relative">
        {/* Search input */}
        <div
          className="d-flex align-items-center gap-2 rounded-3 px-3"
          style={{
            border: "1.5px solid var(--theme-color-mute)",
            backgroundColor: "var(--theme-card-bg)",
            transition: "border-color 0.15s",
          }}
          onFocus={() => {
            /* let input handle its own focus ring */
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--theme-color-body-mid)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            id="label-search-input"
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => trimmedQuery && setIsDropdownOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search a label…"
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              padding: "10px 0",
              fontSize: "15px",
              color: "var(--theme-color-ink)",
              fontFamily: "var(--theme-font-family-body)",
            }}
            aria-label="Search labels"
            aria-autocomplete="list"
            aria-expanded={isDropdownOpen && matchedLabels.length > 0}
            aria-controls="label-search-dropdown"
            aria-activedescendant={
              activeIndex >= 0
                ? `label-option-${activeIndex}`
                : undefined
            }
          />
          {query && (
            <button
              onClick={() => {
                setQuery("");
                setIsDropdownOpen(false);
                setActiveIndex(-1);
                inputRef.current?.focus();
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0 2px",
                color: "var(--theme-color-body-mid)",
                lineHeight: 1,
              }}
              aria-label="Clear search"
              type="button"
            >
              ✕
            </button>
          )}
        </div>

        {/* Dropdown */}
        {isDropdownOpen && trimmedQuery && (
          <div
            ref={dropdownRef}
            id="label-search-dropdown"
            role="listbox"
            aria-label="Matching labels"
            className="position-absolute w-100 rounded-3 shadow-sm overflow-hidden"
            style={{
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              zIndex: 200,
              border: "1px solid var(--theme-color-mute)",
              backgroundColor: "var(--theme-card-bg)",
            }}
          >
            {matchedLabels.length > 0 ? (
              <ul className="m-0 p-0" style={{ listStyle: "none" }}>
                {matchedLabels.map((label, idx) => (
                  <li
                    key={label.id}
                    id={`label-option-${idx}`}
                    role="option"
                    aria-selected={activeIndex === idx}
                    onClick={() => handleSelectLabel(label.name)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "9px 14px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontFamily: "var(--theme-font-family-body)",
                      color: "var(--theme-color-ink)",
                      backgroundColor:
                        activeIndex === idx
                          ? "var(--theme-color-canvas-soft)"
                          : "transparent",
                      transition: "background-color 0.1s",
                      borderBottom: "1px solid var(--theme-color-canvas-soft)",
                    }}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: label.color || "#94a3b8",
                        flexShrink: 0,
                        display: "inline-block",
                      }}
                    />
                    <span>{label.name}</span>
                  </li>
                ))}
              </ul>
            ) : noMatch ? (
              <div
                style={{
                  padding: "14px 16px",
                  fontSize: "14px",
                  color: "var(--theme-color-body-mid)",
                  fontFamily: "var(--theme-font-family-body)",
                  fontStyle: "italic",
                }}
              >
                Label does not exist
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Hint text shown when search box is empty */}
      {!trimmedQuery && (
        <p
          className="mt-4 text-sm"
          style={{ color: "var(--theme-color-body-mid)" }}
        >
          Start typing to see matching labels.
        </p>
      )}
    </div>
  );
}

export default LabelSearchPage;
